import React, { useCallback, useMemo, useState } from "react";
import { sanitizePublicUiError } from "~/productSafeLabels";
import { refreshCmmdForgeRuntimeList } from "../../cmmd/cmmdForgeRuntimeListResource";
import type { SidebarProjectSnapshot } from "./SidebarProjectItem";
import {
  openProjectRemovalToast,
  reportProjectRemovalStep,
  settleProjectRemovalFailed,
  settleProjectRemovalRemoved,
} from "./projectRemovalToast";
import { resolveCloudRemovalPresentation } from "./cloudRemovalPresentation";
import type { RemovalStepLabel } from "./removalStepLabels";
import { createRemovableForgeProject, removeSidebarProject } from "./sidebarProjectRemoval";
import {
  runProjectGroupRemoval,
  type ProjectGroupRemovalResult,
  type ProjectGroupRemovalTarget,
} from "~/projectGroupRemovalRunner";
import {
  executeProjectCheckoutDeletion,
  previewProjectCheckoutDeletion,
} from "~/projectCheckoutDeletionClient";
import {
  resolveSidebarCloudProjectForTarget,
  useSidebarProjectFileRemoval,
} from "./useSidebarProjectFileRemoval";

// Exported only because it appears in this hook's return type, which TypeScript
// cannot name otherwise (TS4058). No consumer imports it.
// fallow-ignore-next-line unused-type
export interface ConfirmRemoveProjectOptions {
  /** Forge Cloud REGISTRATION id — only set for rows that really are cloud rows. */
  readonly cloudRegistrationProjectId?: number | null;
}

export interface UseSidebarProjectRemovalProps {
  readonly project: SidebarProjectSnapshot;
  /** Exact checkout ownership for the dialog's per-target thread counts. */
  readonly projectThreads: readonly {
    readonly environmentId: string;
    readonly projectId: string;
  }[];
}

async function runSidebarFileRemoval(input: {
  readonly project: SidebarProjectSnapshot;
  readonly targets: readonly ProjectGroupRemovalTarget[];
  readonly confirmation: string;
  readonly setResults: (results: readonly ProjectGroupRemovalResult[]) => void;
}): Promise<boolean> {
  const results = await runProjectGroupRemoval({
    targets: input.targets,
    deleteFiles: true,
    confirmation: input.confirmation,
    preview: (target) =>
      previewProjectCheckoutDeletion(
        target,
        resolveSidebarCloudProjectForTarget(input.project, target),
      ),
    deleteFilesForCheckout: async (target, deletion) => {
      const cloudProject = resolveSidebarCloudProjectForTarget(input.project, target);
      return {
        deletion: await executeProjectCheckoutDeletion({
          target,
          ...deletion,
          ...(cloudProject ? { cloudProject } : {}),
        }),
        // Whether that call ALSO removed the registration, which decides
        // whether the runner then runs a second removal step. The client
        // reports no such field, so this mirrors the branch it took: a `cloud`
        // target goes through `removeSidebarProject`, which unregisters or
        // throws, while a local one only deletes files.
        //
        // On the id, the two read the same expression,
        // `classifyEnvironmentId(...).isCloud` — see
        // projectCheckoutDeletionClient.test.ts for a durable row. `kind` also
        // ORs in `project.preConnectCloud` (useSidebarProjectFileRemoval.ts),
        // which the client answers with its own `cloudProject` argument rather
        // than the id; that arm is the one to check if these ever drift.
        metadataRemoved: target.kind === "cloud",
      };
    },
    removeMetadata: (target) =>
      removeSidebarProject(createRemovableForgeProject([target]), { destroyRuntime: false }),
  });
  input.setResults(results);
  const completed = results.every((result) => result.metadata === "removed");
  if (!completed) refreshCmmdForgeRuntimeList();
  return completed;
}

function beginSidebarRemoval(input: {
  readonly project: SidebarProjectSnapshot;
  readonly targets: readonly ProjectGroupRemovalTarget[];
  readonly options: ConfirmRemoveProjectOptions | undefined;
  readonly fileRemoval:
    | { readonly deleteFiles: boolean; readonly confirmation: string }
    | undefined;
  readonly setRemoving: (value: boolean) => void;
  readonly setStep: (step: RemovalStepLabel | null) => void;
  readonly setResults: (results: readonly ProjectGroupRemovalResult[]) => void;
}): void {
  const toastTarget = { projectKey: input.project.projectKey, projectName: input.project.name };
  void (async () => {
    input.setRemoving(true);
    input.setStep(null);
    openProjectRemovalToast(toastTarget);
    try {
      const completed = input.fileRemoval?.deleteFiles
        ? await runSidebarFileRemoval({
            project: input.project,
            targets: input.targets,
            confirmation: input.fileRemoval.confirmation,
            setResults: input.setResults,
          })
        : await removeSidebarProject(input.project, {
            ...input.options,
            destroyRuntime: false,
            onProgress: (step) => {
              input.setStep(step);
              reportProjectRemovalStep({ ...toastTarget, stepLabel: step });
            },
          }).then(() => {
            input.setResults(
              input.targets.map((target) => ({
                target,
                fileDeletion: "not-requested" as const,
                metadata: "removed" as const,
              })),
            );
            return true;
          });
      if (!completed) return;
      settleProjectRemovalRemoved(toastTarget);
    } catch (error) {
      refreshCmmdForgeRuntimeList();
      settleProjectRemovalFailed({
        ...toastTarget,
        description: sanitizePublicUiError(error, "Unknown error removing project."),
      });
    } finally {
      input.setRemoving(false);
      input.setStep(null);
    }
  })();
}

function retryCloudRemoval(
  event: React.MouseEvent<HTMLButtonElement>,
  failure: ReturnType<typeof resolveCloudRemovalPresentation>,
  confirm: (options: ConfirmRemoveProjectOptions) => void,
): void {
  event.preventDefault();
  event.stopPropagation();
  if (failure.kind !== "retryable" || failure.projectId === null) return;
  confirm({ cloudRegistrationProjectId: failure.projectId });
}

export function resolveSidebarRemovalState(
  project: SidebarProjectSnapshot,
  isRemovingLocally: boolean,
  localStep: RemovalStepLabel | null,
) {
  const preConnectCloud = project.preConnectCloud ?? null;
  const presentation = resolveCloudRemovalPresentation(
    project.cloudRemoval ?? preConnectCloud?.removal ?? null,
    project.cloudRegistrationProjectId ?? preConnectCloud?.cmmdProjectId ?? null,
  );
  return {
    presentation,
    isRemovingProject: isRemovingLocally || presentation.kind !== "none",
    isRemovalActivelyRunning: isRemovingLocally || presentation.kind === "active",
    removalStepLabel: localStep ?? (presentation.kind === "none" ? null : presentation.stepLabel),
    removalFailure: presentation.kind === "retryable" ? presentation : null,
  };
}

/**
 * Removing one project from a sidebar row: its dialog, its progress, its retry.
 *
 * Split out of `useSidebarProjectItem` because removal is the one thing on that
 * row with a lifetime longer than the row itself. A cloud removal is a durable
 * CMMD operation that outlives a reload, so its state arrives from two places
 * at once — local `useState` while the click is still in flight, and CMMD's
 * persisted receipt afterwards — and reconciling those two is most of what is
 * below. Renaming a thread has no such shape.
 */
export function useSidebarProjectRemoval({
  project,
  projectThreads,
}: UseSidebarProjectRemovalProps) {
  const [removeProjectDialogOpen, setRemoveProjectDialogOpen] = useState(false);
  const [isRemovingProjectLocally, setIsRemovingProjectLocally] = useState(false);
  /**
   * The removal step currently in flight, or `null` when nothing is running.
   *
   * Lives on the ROW rather than in the dialog on purpose: the dialog is
   * dismissible (Escape), and a multi-minute durable operation must not end up
   * with no surface reporting it. Both the dialog and row also hydrate from
   * CMMD's persisted catalog overlay after reload.
   */
  const [localRemovalStepLabel, setLocalRemovalStepLabel] = useState<RemovalStepLabel | null>(null);
  const fileRemovalState = useSidebarProjectFileRemoval(project, projectThreads);

  const removalState = resolveSidebarRemovalState(
    project,
    isRemovingProjectLocally,
    localRemovalStepLabel,
  );

  // Blast-radius summary for the Remove dialog. A row is a LOGICAL project: it
  // aggregates every physical checkout in `worktreeMembers`, and "Remove project"
  // removes ALL of them. Surface what will actually go (which checkouts, how many
  // threads) so a wrongly-grouped checkout is visible BEFORE the user confirms.
  const removalSummary = useMemo(() => {
    const checkouts = project.worktreeMembers.map((member) => ({
      label: member.label,
      cwd: member.cwd,
      environmentRole: member.environmentRole,
    }));
    return {
      checkoutCount: checkouts.length > 0 ? checkouts.length : project.memberProjectRefs.length,
      checkouts,
      threadCount: projectThreads.length,
    };
  }, [project.worktreeMembers, project.memberProjectRefs.length, projectThreads.length]);

  const handleRemoveProjectClick = useCallback(
    (event: React.MouseEvent) => {
      event.preventDefault();
      event.stopPropagation();
      fileRemovalState.reset();
      setRemoveProjectDialogOpen(true);
    },
    [fileRemovalState],
  );

  /**
   * Run the removal and REPORT IT — the whole way through, and to a terminal
   * state either way.
   *
   * Live defects this shape exists for:
   *  (C) provider destruction may take several minutes, but CMMD persists the
   *      receipt and continues after reload. Local state gives immediate
   *      feedback until the catalog overlay becomes authoritative.
   *  (A) the failure branch called `toastManager.add()` and threw away the id,
   *      so a failure toast could never be retracted once its subject was
   *      actually removed. `projectRemovalToast` keys one toast per project so
   *      a later outcome replaces the earlier one.
   *
   * The toast bookkeeping is module-level rather than a ref because a
   * successful removal UNMOUNTS this row — a ref would be destroyed at exactly
   * the moment the success toast needs to be raised.
   */
  const handleConfirmRemoveProject = useCallback(
    (
      options?: ConfirmRemoveProjectOptions,
      fileRemoval?: { readonly deleteFiles: boolean; readonly confirmation: string },
    ) => {
      beginSidebarRemoval({
        project,
        targets: fileRemovalState.targets,
        options,
        fileRemoval,
        setRemoving: setIsRemovingProjectLocally,
        setStep: setLocalRemovalStepLabel,
        setResults: fileRemovalState.setResults,
      });
    },
    [fileRemovalState, project],
  );

  const handleRemoveProjectDialogOpenChange = useCallback(
    (open: boolean) => {
      if (!open) fileRemovalState.reset();
      setRemoveProjectDialogOpen(open);
    },
    [fileRemovalState],
  );

  const handleRetryCloudRemoval = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) =>
      retryCloudRemoval(event, removalState.presentation, handleConfirmRemoveProject),
    [handleConfirmRemoveProject, removalState.presentation],
  );

  return {
    removeProjectDialogOpen,
    setRemoveProjectDialogOpen: handleRemoveProjectDialogOpenChange,
    isRemovingProject: removalState.isRemovingProject,
    isRemovalActivelyRunning: removalState.isRemovalActivelyRunning,
    removalStepLabel: removalState.removalStepLabel,
    removalFailure: removalState.removalFailure,
    removalSummary,
    fileRemovalResults: fileRemovalState.results,
    fileRemovalTargets: fileRemovalState.targets,
    fileRemovalPreviews: fileRemovalState.previews,
    isPreviewingFiles: fileRemovalState.isPreviewing,
    handleDeleteFilesChange: fileRemovalState.onDeleteFilesChange,
    handleRemoveProjectClick,
    handleConfirmRemoveProject,
    handleRetryCloudRemoval,
  };
}
